import LoadingIcon from "@svg/loadingIcon";
import { IDefaultTermInput, ITermDetailInput } from "@types";
import { commonApi } from "gate";
import Image from "next/legacy/image";
import Link from "next/link";
import { SwiperSlide } from "swiper/react";
import SwiperCore, { FreeMode, Scrollbar, Mousewheel } from "swiper";
import { useRouter } from "next/router";
import { RefObject, useEffect, useRef, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { useTranslations } from "use-intl";
import SearchIcon from "@svg/SearchIcon";
import PathIcon from "@svg/PathIcon";
import CardProduct from "@elements/productCard/productCard";
import { segmentEvent } from "@utils/segment";
import { useAppContext } from "@context/app";
import Carousel from "@elements/carousel";

interface SearchBoxProps {
  isMobile?: boolean;
}

function useOutsideAlerter(ref: RefObject<any>, callback: () => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
}

const SearchView: React.FC<SearchBoxProps> = ({ isMobile }) => {
  SwiperCore.use([FreeMode, Scrollbar, Mousewheel]);
  const { locale } = useRouter();
  const { user, country } = useAppContext()
  const t = useTranslations("");
  const [data, setData] = useState<ITermDetailInput | undefined>();
  const [defaultData, setDefaultData] = useState<IDefaultTermInput[]>([]);
  const [suggestions, setSuggestions] = useState<IDefaultTermInput[]>([]);
  const [viewResult, setViewResult] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, () => {
    handelClose();
  });

  useEffect(() => { fetchTerms() }, [])

  const fetchTerms = () => {

    setIsLoading(true);
    commonApi
      .getTerms()
      .fetch()
      .then((res) => {
        setDefaultData(res.data.items);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  let _debounceSearch: any = undefined;

  const debounceSearch = (input: string) => {
    if (_debounceSearch) clearTimeout(_debounceSearch);
    _debounceSearch = setTimeout(() => {
      getSuggestions(input);
    }, 300);
  };

  const getSuggestions = async (input: string) => {
    if (input.length >= 3) {
      try {
        setIsLoading(true);
        const res = await commonApi.getTermSuggestion({ term: input }).fetch();
        setSuggestions(res.data.items);
        setIsLoading(false);
        setViewResult(false);
      } catch (error) {
        setIsLoading(false);
      }
    }
  };
  const search = async (input: string, action?: string) => {
    if (input.length >= 3) {
      try {
        setIsLoading(true);
        commonApi.setTerm({ term: input }).fetch();
        const res = await commonApi.getTermDetail({ term: input }).fetch();
        segmentEvent("Product Searched", {
          action: action ?? "Search Box",
          search_keyword: input,
          user_id: user.data?.id || -1,
          language: locale?.includes("en") ? "English" : "Arabic",
          country_id: country?.id,
          country_slug: country?.slug,
          from_page: "search",
          from_entity: "",
          from_entity_slug: "",
          from_page_section: action ?? "Search Box",
          from_page_section_entity: "",
          from_page_section_slug: "",
        });
        setData(res.data);
        setViewResult(true);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    }
  };

  const handelClose = () => {
    setInputValue("");
    setData(undefined);
    setSuggestions([]);
    setViewResult(false);
    setIsLoading(false);
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      search(inputValue.trim());
    }
  };
  return (
    <div
      className={`w-full min-h-[375px] max-w-md mx-auto rounded-xl flex transition-all duration-300 ease-linear whitespace-nowrap`}
    >
      <div className={`w-full bg-white pt-3`} >
        <div className="w-full px-3 mb-3">
          <div className="w-full bg-[#f4f4f4] h-[45px] rounded-xl flex flex-nowrap items-center justify-between px-2">
            <input
              type="search"
              value={inputValue}
              className="flex-1 bg-[#f4f4f4] h-[45px] p-1 rounded-xl border-0 outline-none text-sm"
              placeholder={t("client_search_searchanything")}
              required
              onChange={(e) => {
                setInputValue(e.target.value);
                debounceSearch(e.target.value.trim());
              }}
              onKeyPress={(event: any) => handleKeyPress(event)}
            />
            {!isLoading ? (
              <BsSearch size={18} className="mx-1" />
            ) : (
              <LoadingIcon />
            )}
          </div>
        </div>

        {viewResult && data && inputValue ? (
          data.items.length ? (
            data.items.map((item) => (
              <div className="w-full" key={`termDetail-${item.type}-${item.items.length}`}>
                {item.type === "suggestion" && (
                  <div className="w-full mb-3 px-3">
                    {item.items.map((termItem) => (
                      <p
                        key={`termItem-${termItem.name}`}
                        className="flex items-center font-normal text-xs text-[#333] mb-1 cursor-pointer"
                        onClick={() => {
                          setInputValue(termItem.name);
                          search(termItem.name);
                        }}
                      >
                        <SearchIcon className="px-1" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                          {termItem.name}
                        </span>
                      </p>
                    ))}
                  </div>
                )}
                {item.type === "banner" && (
                  <div className="w-full mb-3 px-3">
                    <Image src={item.image_url || ""} width={400} height={172} alt='bear' />
                  </div>
                )}

                {item.type === "product" && (
                  <div className="w-full mb-3 overflow-hidden px-2">
                    <p className="font-normal text-sm text-[#333] pb-2 px-2 whitespace-nowrap overflow-hidden text-ellipsis">{`${item.header.name}`}</p>
                    <div className="w-full pb-3">
                      <Carousel gap={10}>
                        {item.items.map((product) => (
                          <SwiperSlide key={product.default_sku_id} className="w-max">
                            <CardProduct
                              searchAction={() => {
                                handelClose();
                              }}
                              product={product}
                              width={180}
                              source="search"
                              section={"Search Results"}
                              q={inputValue}
                            />
                          </SwiperSlide>
                        ))}
                        {item.header.more && (
                          <SwiperSlide className="w-max">
                            <div className="sm:mx-0 xs:mx-auto mx-auto">
                              <Link
                                href={`/product?input=${inputValue}`}

                                prefetch={false}
                                onClick={handelClose}
                                className=""
                              >

                                <div className="min-w-[104px] w-[104px] min-h-[296px] p-3 rounded-2xl border border-[#eee] flex justify-center items-center flex-col">
                                  <PathIcon className="mb-3" />{" "}
                                  <p className="text-[#008AF6] font-normal text-sm">
                                    {item.header.more.name}
                                  </p>
                                </div>

                              </Link>
                            </div>
                          </SwiperSlide>
                        )}
                      </Carousel>
                    </div>
                  </div>
                )}

                {item.type === "category" && (
                  <div className="w-full mb-3 overflow-hidden px-2">
                    <p className="font-normal text-sm text-[#333] pb-2 px-2 whitespace-nowrap overflow-hidden text-ellipsis">{`${item.header.name}`}</p>
                    <div className="w-full pb-3">
                      <Carousel>
                        {item.items.map((category) => (
                          <SwiperSlide
                            key={`category-${category?.id}`}
                            className="w-max"

                          >
                            <Link
                              href={`/product?category_id=${category?.id}`}

                              prefetch={false}
                              onClick={() => {
                                segmentEvent("Category Viewed", {
                                  user_id: user.data?.id || -1,
                                  country_id: country?.id,
                                  country_slug: country?.slug,
                                  language: locale?.includes("en") ? "English" : "Arabic",
                                  category_id: category.id,
                                  category_slug: category.slug,
                                  from_page: "search",
                                  from_entity: "",
                                  from_entity_slug: "",
                                  from_page_section: "",
                                  from_page_section_entity: "",
                                  from_page_section_slug: "",
                                });
                                handelClose()
                              }}
                            >

                              <div className="min-w-[152px] flex justify-center items-end h-[160px] px-1">
                                <div
                                  className={`w-[144px] h-[104px] rounded-2xl`}
                                  style={{
                                    backgroundColor: category.color,
                                  }}
                                >
                                  <div className="w-full h-[50px] relative">
                                    <div className="absolute bottom-0 left-[22px]">
                                      <div className="w-[100px] h-[100px] relative">
                                        <Image
                                          src={category.icon.url}
                                          alt={category.name}
                                          layout="fill"
                                          objectFit={"fill"}
                                          placeholder="blur"
                                          blurDataURL={category.icon.url}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="w-full h-[50px] flex items-center justify-center">
                                    <p className="font-normal text-sm text-[#333]">
                                      {category.name}
                                    </p>
                                  </div>
                                </div>
                              </div>

                            </Link>
                          </SwiperSlide>
                        ))}
                      </Carousel>
                    </div>
                  </div>
                )}
                {item.type === "brand" && (
                  <div className="w-full mb-3 overflow-hidden px-2">
                    <p className="font-normal text-sm text-[#333] pb-2 px-2 whitespace-nowrap overflow-hidden text-ellipsis">{`${item.header.name}`}</p>
                    <div className="w-full pb-3">
                      <Carousel>
                        {item.items.map((brand: any) => (
                          <SwiperSlide key={`brand-${brand.id}`} className="w-max">
                            <Link
                              href={brand.brand_type === "onecard" ? `/voucher/${brand.slug}` : `/product?brand_id=${brand.id}`}
                              prefetch={false}
                              onClick={() => {
                                segmentEvent("Brand Viewed", {
                                  user_id: user.data?.id || -1,
                                  country_id: country?.id,
                                  country_slug: country?.slug,
                                  language: locale?.includes("en") ? "English" : "Arabic",
                                  brand_id: brand.id,
                                  brand_slug: brand.slug,
                                  from_page: "search",
                                  from_entity: "",
                                  from_entity_slug: "",
                                  from_page_section: "",
                                  from_page_section_entity: "",
                                  from_page_section_slug: "",
                                });
                                handelClose()
                              }}
                            >

                              <div className="min-w-[88px] h-[88px] relative border rounded-2xl overflow-hidden mx-1">
                                <div className="h-full w-full relative overflow-hidden flex items-center justify-center scale-1 transition-transform duration-200 ease-in-out hover:scale-[1.05]">
                                  <Image
                                    src={brand.media.url}
                                    className=""
                                    alt={brand.name}
                                    width={75}
                                    height={75}
                                    objectFit={"fill"}
                                    placeholder={"blur"}
                                    blurDataURL={brand.media.url}
                                  />
                                </div>
                              </div>

                            </Link>
                          </SwiperSlide>
                        ))}
                      </Carousel>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="w-full flex flex-col justify-center items-center px-5 h-[250px]">
              <SearchIcon className="w-10 h-10" />
              <p className=" text-sm font-bold text-[#999] mb-3 w-full whitespace-normal text-center mt-2 leading-6">
                {`Couldn't find what you're looking for. Search something else.`}{" "}
              </p>
            </div>
          )
        ) : suggestions.length && inputValue ? (
          <div className="w-full p-3 pt-1">
            {suggestions.map((term) => (
              <div className="w-full mb-3" key={`term-${term.type}-${term.layout}`}>
                {term.header && (
                  <p className=" font-normal text-sm text-[#333] pb-2">
                    {term.header.name}
                  </p>
                )}
                {term.items.map((termItem) => (
                  <p
                    key={`termItem-${termItem.name}`}
                    className=" flex items-center font-normal text-xs text-[#333] mb-1 cursor-pointer"
                    onClick={() => {
                      setInputValue(termItem.name);
                      search(termItem.name);
                    }}
                  >
                    <SearchIcon className=" px-1" />
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                      {termItem.name}
                    </span>
                  </p>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full p-3 pt-1">
            {defaultData.length
              ? defaultData.map((term) => (
                <div className="w-full mb-3" key={`term-${term.type}-${term.header?.name}`}>
                  <p className="font-normal text-sm text-[#333] pb-2">
                    {term.header?.name}
                  </p>
                  {term.type === "product" ? (
                    <div className="w-full pb-3">
                      <Carousel gap={10}>
                        {term.items.map((product: any) => (
                          <SwiperSlide
                            key={product.default_sku_id}
                            className="w-max"
                          >
                            <CardProduct
                              searchAction={() => {
                                handelClose();
                                segmentEvent("Product Searched", {
                                  action: "Popular Searches",
                                  search_keyword: inputValue,
                                  user_id: user.data?.id || -1,
                                  language: locale?.includes("en") ? "English" : "Arabic",
                                  country_id: country?.id,
                                  country_slug: country?.slug,
                                  from_page: "search",
                                  from_entity: "",
                                  from_entity_slug: "",
                                  from_page_section: "Popular Searches",
                                  from_page_section_entity: "",
                                  from_page_section_slug: "",
                                });
                              }}
                              width={180}
                              product={product}
                              source="search"
                              section={"Popular Searches"}
                            />
                          </SwiperSlide>
                        ))}
                      </Carousel>
                    </div>
                  ) : (
                    term.items.map((termItem) => (
                      <p
                        key={`termItem-${termItem.name}`}
                        className=" flex items-center font-normal text-xs text-[#333] mb-1 cursor-pointer"
                        onClick={() => {
                          setInputValue(termItem.name);
                          search(termItem.name, term.e?.v);
                        }}
                      >
                        <SearchIcon className=" px-1" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                          {termItem.name}
                        </span>
                      </p>
                    ))
                  )}
                </div>
              ))
              : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchView;
